import type { ReactNode } from "react"
import type { ListChildComponentProps } from "react-window"
import { memo, useCallback, useMemo, useState } from "react"
import { areEqual, VariableSizeList as List } from "react-window"

import {
	EMOJI_CATEGORIES,
	normalizeEmojiQuery,
	searchEmojis,
} from "@productlane/lib/src/emoji"
import { reactionGlyph } from "@productlane/lib/src/message-reactions"
import { Button, Empty, Input, Popover, Tooltip } from "@productlane/ui"

import { EmptySearchIllustration } from "@/components/empty-search-illustration"
import { api } from "@/lib/api"
import {
	readFrequentEmoji,
	recordEmojiUse,
	topFrequentEmoji,
} from "@/lib/frequent-emoji"
import { useAuthData } from "@/lib/zero"

export interface PickedEmoji {
	/** Slack-style name without colons, e.g. "+1" or a custom emoji's name */
	name: string
	imageUrl: string | null
}

export interface PickerEntry extends PickedEmoji {
	glyph: string | null
}

export type PickerRow =
	| { kind: "header"; key: string; label: string }
	| { kind: "emoji"; key: string; entries: PickerEntry[] }
	| { kind: "end"; key: string }

export const PICKER_COLUMNS = 9
const CELL_SIZE = 36
const HEADER_HEIGHT = 30
const END_HEIGHT = 8
const LIST_HEIGHT = 320
const SEARCH_LIMIT = 200

export const EMOJI_PICKER_COPY = {
	search: "Search emoji...",
	frequent: "Frequently used",
	custom: "Custom",
	emptyTitle: "No emoji found",
	emptyText: "Try another name",
}

function rowsOf(
	key: string,
	entries: PickerEntry[],
	columns: number,
): PickerRow[] {
	const rows: PickerRow[] = []
	for (let i = 0; i < entries.length; i += columns) {
		rows.push({
			kind: "emoji",
			key: `${key}-${i}`,
			entries: entries.slice(i, i + columns),
		})
	}
	return rows
}

function section(
	key: string,
	label: string,
	entries: PickerEntry[],
	columns: number,
) {
	if (entries.length === 0) return []
	return [
		{ kind: "header", key: `${key}-header`, label } as const,
		...rowsOf(key, entries, columns),
	]
}

/** Frequently used, Slack's categories, then the workspace's custom emoji; a search shows one flat grid. */
export function emojiPickerRows({
	query,
	custom,
	frequent,
	columns = PICKER_COLUMNS,
}: {
	query: string
	custom: readonly { name: string; url: string }[]
	frequent: readonly PickedEmoji[]
	columns?: number
}): PickerRow[] {
	const customEntries = custom.map((emoji) => ({
		name: emoji.name,
		glyph: null,
		imageUrl: emoji.url,
	}))
	const normalized = normalizeEmojiQuery(query)

	if (normalized) {
		const seen = new Set<string>()
		const matches: PickerEntry[] = []
		const add = (name: string, glyph: string) => {
			if (seen.has(glyph)) return
			seen.add(glyph)
			matches.push({ name, glyph, imageUrl: null })
		}
		for (const entry of searchEmojis(normalized, SEARCH_LIMIT)) {
			add(entry.name, entry.emoji)
		}
		for (const category of EMOJI_CATEGORIES) {
			for (const [name, glyph, ...aliases] of category.emojis) {
				if ([name, ...aliases].some((alias) => alias.includes(normalized))) {
					add(name, glyph)
				}
			}
		}
		const customMatches = customEntries.filter((entry) =>
			entry.name.includes(normalized),
		)
		const found = [...matches, ...customMatches]
		return found.length > 0
			? [...rowsOf("search", found, columns), { kind: "end", key: "end" }]
			: []
	}

	const frequentEntries = frequent.map((emoji) => ({
		...emoji,
		glyph: emoji.imageUrl ? null : (reactionGlyph(emoji.name) ?? null),
	}))
	return [
		...section(
			"frequent",
			EMOJI_PICKER_COPY.frequent,
			frequentEntries,
			columns,
		),
		...EMOJI_CATEGORIES.flatMap((category) =>
			section(
				category.id,
				category.label,
				category.emojis.map(([name, glyph]) => ({
					name,
					glyph,
					imageUrl: null,
				})),
				columns,
			),
		),
		...section("custom", EMOJI_PICKER_COPY.custom, customEntries, columns),
		{ kind: "end", key: "end" },
	]
}

export function EmojiGlyph({
	glyph,
	imageUrl,
	name,
	size = "sm",
}: {
	glyph: string | null
	imageUrl: string | null
	name: string
	size?: "sm" | "lg"
}) {
	if (imageUrl) {
		return (
			<img
				src={imageUrl}
				alt={`:${name}:`}
				className={
					size === "lg"
						? "size-[22px] flex-none object-contain"
						: "size-4 flex-none object-contain"
				}
				draggable={false}
			/>
		)
	}
	return (
		<span
			className={
				size === "lg" ? "text-[22px] leading-none" : "text-[15px] leading-none"
			}
		>
			{glyph ?? `:${name}:`}
		</span>
	)
}

interface RowData {
	rows: PickerRow[]
	pick: (entry: PickerEntry) => void
}

const Row = memo(({ index, style, data }: ListChildComponentProps<RowData>) => {
	const row = data.rows[index]
	if (!row || row.kind === "end") return <div style={style} />
	if (row.kind === "header") {
		return (
			<div
				style={style}
				className="flex items-end px-3.5 pb-1.5 text-xs font-medium text-secondary"
			>
				{row.label}
			</div>
		)
	}
	return (
		<div style={style} className="flex px-2">
			{row.entries.map((entry) => (
				<Tooltip key={entry.name} label={`:${entry.name}:`}>
					<Button
						variant="ghost"
						aria-label={entry.name}
						className="size-9 min-h-9 flex-none rounded-md p-0"
						onClick={() => data.pick(entry)}
					>
						<EmojiGlyph
							glyph={entry.glyph}
							imageUrl={entry.imageUrl}
							name={entry.name}
							size="lg"
						/>
					</Button>
				</Tooltip>
			))}
		</div>
	)
}, areEqual)

/** The searchable emoji grid on its own, for a popover or a tab of another picker. */
export function EmojiPickerPanel({
	onPick,
	columns = PICKER_COLUMNS,
	withCustom = true,
	plainSearch = false,
}: {
	onPick: (emoji: PickedEmoji) => void
	columns?: number
	withCustom?: boolean
	plainSearch?: boolean
}) {
	const { userId } = useAuthData()
	const [query, setQuery] = useState("")
	const customQuery = api.workspace.slackCustomEmoji.useQuery(undefined, {
		enabled: withCustom,
		staleTime: 10 * 60 * 1000,
	})
	const frequent = useMemo(
		() =>
			topFrequentEmoji(readFrequentEmoji(userId)).filter(
				(emoji) => withCustom || !emoji.imageUrl,
			),
		[userId, withCustom],
	)
	const rows = useMemo(
		() =>
			emojiPickerRows({
				query,
				custom: withCustom ? (customQuery.data ?? []) : [],
				frequent,
				columns,
			}),
		[query, customQuery.data, frequent, columns, withCustom],
	)

	const pick = useCallback(
		(entry: PickerEntry) => {
			const picked = { name: entry.name, imageUrl: entry.imageUrl }
			recordEmojiUse(userId, picked)
			onPick(picked)
			setQuery("")
		},
		[onPick, userId],
	)
	const itemData = useMemo<RowData>(() => ({ rows, pick }), [rows, pick])
	const firstEntry = rows.find((row) => row.kind === "emoji")?.entries[0]
	const width = columns * CELL_SIZE + 16

	return (
		<div style={{ width }}>
			{plainSearch ? (
				<div className="border-b border-gray-200 px-4 py-2.5 dark:border-gray-700/80">
					<input
						name="emoji-search"
						value={query}
						placeholder={EMOJI_PICKER_COPY.search}
						autoFocus
						autoComplete="off"
						onChange={(event) => setQuery(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter" && firstEntry) {
								event.preventDefault()
								pick(firstEntry)
							}
						}}
						className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
					/>
				</div>
			) : (
				<div className="border-b border-gray-200 px-1.5 py-1.5 dark:border-gray-700/80">
					<Input
						name="emoji-search"
						type="text"
						variant="borderless"
						placeholder={EMOJI_PICKER_COPY.search}
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter" && firstEntry) {
								event.preventDefault()
								pick(firstEntry)
							}
						}}
						inputClassName="dark:!bg-gray-850"
						autoComplete="off"
						autoFocus
					/>
				</div>
			)}
			{rows.length > 0 ? (
				<List
					key={query}
					height={LIST_HEIGHT}
					width={width}
					itemCount={rows.length}
					itemSize={(index) => {
						const row = rows[index]
						if (row?.kind === "header") return HEADER_HEIGHT
						if (row?.kind === "end") return END_HEIGHT
						return CELL_SIZE
					}}
					itemKey={(index) => rows[index]?.key ?? index}
					itemData={itemData}
				>
					{Row}
				</List>
			) : (
				<div
					className="flex items-center justify-center"
					style={{ height: LIST_HEIGHT }}
				>
					<Empty
						title={EMOJI_PICKER_COPY.emptyTitle}
						text={EMOJI_PICKER_COPY.emptyText}
						image={<EmptySearchIllustration />}
					/>
				</div>
			)}
		</div>
	)
}

export function EmojiPicker({
	children,
	onSelect,
	align = "end",
	tooltip,
}: {
	children: ReactNode
	onSelect: (emoji: PickedEmoji) => void
	align?: "start" | "center" | "end"
	tooltip?: ReactNode
}) {
	const [open, setOpen] = useState(false)
	const pick = useCallback(
		(emoji: PickedEmoji) => {
			onSelect(emoji)
			setOpen(false)
		},
		[onSelect],
	)

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			align={align}
			triggerTooltip={tooltip}
			className="w-auto overflow-hidden p-0"
			content={open ? <EmojiPickerPanel onPick={pick} /> : null}
		>
			{children}
		</Popover>
	)
}
